import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  useEmergencyLoginMutation,
  useLazyGetFireFighterAuthorizeQuery,
  EmergencyLoginResponse,
} from '../../api/auth/AuthApi'
import {
  setAtlasData,
  setRefreshToken,
} from '../../redux/reducers/AtlasSandReducer'
import {
  UI_CONSTANTS,
  FF_SESSION_KEY,
} from '../../utils/constants/apiConstants'
import { showToast } from '../../components/common/toast/Toast'

export const useFirefighterLogin = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['translation'])
  const [emergencyLogin] = useEmergencyLoginMutation()
  const [getFireFighterAuthorize] = useLazyGetFireFighterAuthorizeQuery()

  const displayErrorToast = (message: string, code: number | string) => {
    showToast(`${message}(${code})`, 'error')
  }

  const handleLogin = (
    userEmail: string,
    password: string,
    onDone: () => void,
  ) => {
    emergencyLogin({
      userEmail,
      password,
      clientId: process.env.REACT_APP_CLIENT_ID || '',
    })
      .unwrap()
      .then((loginData: EmergencyLoginResponse) => {
        // /Login returns { code, data: { accessToken, refreshToken }, totalCount, requestStatus }
        if (!loginData?.requestStatus) {
          displayErrorToast(t(`messages.${loginData?.code}`), loginData?.code)
          return
        }
        const { accessToken, refreshToken } = loginData.data
        // Call /FireFighterAuthorize with the accessToken to get full user/permissions data
        return getFireFighterAuthorize(accessToken)
          .unwrap()
          .then((authorizeData) => {
            // /FireFighterAuthorize returns { code, data: <userdata>, totalCount, requestStatus }
            if (!authorizeData?.requestStatus) {
              displayErrorToast(
                t(`messages.${authorizeData?.code}`),
                authorizeData?.code,
              )
              return
            }
            localStorage.setItem(
              FF_SESSION_KEY,
              JSON.stringify({ accessToken, refreshToken }),
            )
            dispatch(setRefreshToken(refreshToken))
            dispatch(
              setAtlasData({
                method: UI_CONSTANTS.AUTHORIZED_USER,
                data: authorizeData.data,
              }),
            )
            // Redirect away from /firefighter so the app renders the default dashboard route
            window.location.replace('/')
          })
      })
      .catch((error: { status: number }) => {
        console.error('Emergency login failed:', error)
        displayErrorToast(t(`messages.${error?.status}`), error?.status)
      })
      .finally(onDone)
  }

  return { handleLogin }
}
